import React from "react";

/**
 * Lightweight error boundary for isolated sub-trees (modals, widgets, etc.).
 *
 * Catches any uncaught render-phase exception and shows a small, on-brand
 * fallback instead of letting the whole app go black. Designed to wrap
 * components whose failure should NOT crash the rest of the page (e.g. the
 * image cropper, third-party widgets).
 *
 * Pure CSS — no external deps — so it always renders even if Tailwind /
 * Framer fails to load.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so we can debug in production via browser console.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", error, info);
  }

  reset = () => {
    this.setState({ error: null });
    if (typeof this.props.onReset === "function") {
      try { this.props.onReset(); } catch { /* ignore */ }
    }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const fallback = this.props.fallback;
    if (typeof fallback === "function") {
      return fallback({ error, reset: this.reset });
    }
    return (
      <div
        style={{
          maxWidth: 480,
          margin: "24px auto",
          padding: 20,
          background: "#0d0e1c",
          border: "1px solid rgba(244, 63, 94, 0.4)",
          borderRadius: 12,
          color: "#fda4af",
          fontFamily: "'Courier New', monospace",
          fontSize: 13,
        }}
        role="alert"
        data-testid="error-boundary-fallback"
      >
        <p style={{ fontWeight: 700, marginBottom: 6, color: "#fecaca" }}>
          ⚠ Algo falló
        </p>
        <p style={{ fontSize: 12, lineHeight: 1.4, marginBottom: 12, color: "rgba(255,255,255,0.55)" }}>
          {(error && error.message) || "Error inesperado en este componente."}
        </p>
        <button
          type="button"
          onClick={this.reset}
          style={{
            background: "rgba(245,166,35,0.12)",
            border: "1px solid rgba(245,166,35,0.45)",
            color: "#f5a623",
            padding: "8px 14px",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
